import { LoaderCircle, Sparkles } from "lucide-react";
import {
  useContext, useEffect, useId, useRef, useState,
} from "react";
import { CampaignVisitContext } from "@/shared/components/CampaignTeaserModal/campaignVisitContext";
import { useCurrentOffers } from "@/shared/hooks/useCurrentOffers";
import { FeatureTeasingService } from "@/shared/services/featureTeasing.service";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/ui/Popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/shared/ui/Tooltip";

type Props = { collapsed: boolean, onBeforeOpen?: () => void };
export const CurrentOffersEntry = ({ collapsed, onBeforeOpen }: Props) => {
  const offers = useCurrentOffers();
  const visit = useContext(CampaignVisitContext);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const trigger = useRef<HTMLButtonElement>(null);
  const intent = useRef<{ campaignId: string, uuid: string } | null>(null);
  const active = useRef(true);
  const selecting = useRef(false);
  const popoverId = useId();
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const reportFailure = (failure: unknown) => {
    const status = (failure as { response?: { status?: number } })?.response?.status;
    const unavailable = status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;
    setError(unavailable ? "Cette offre n’est plus disponible." : "Impossible d’ouvrir l’offre. Réessayez.");
    if (unavailable) {
      intent.current = null;
      void offers.refetch();
    }
  };
  const open = async (campaignId: string) => {
    if (!visit?.beginManual()) return;
    setBusy(true);
    setError("");
    selecting.current = true;
    setExpanded(false);
    if (intent.current?.campaignId !== campaignId) intent.current = { campaignId, uuid: crypto.randomUUID() };
    const reservation = intent.current;
    try {
      // A request abandoned by the UI may still reserve on the server.
      await visit.reservation?.pending?.catch(() => undefined);
      if (!active.current) return;
      const result = await FeatureTeasingService.manualOpen(offers.organizationId, campaignId, reservation.uuid);
      if (!active.current) return;
      onBeforeOpen?.();
      visit.present(result, trigger.current);
      intent.current = null;
    }
    catch (failure) {
      if (!active.current) return;
      reportFailure(failure);
    }
    finally {
      visit.endManual();
      if (active.current) setBusy(false);
    }
  };
  const listUnavailable = !offers.data && (offers.isError || busy);
  if (!visit || (offers.campaigns.length === 0 && !error && !listUnavailable)) return null;
  const multiple = offers.campaigns.length > 1;
  const button = (
    <button
      ref={trigger}
      type="button"
      aria-label="Offres du moment"
      title={listUnavailable ? "Chargement impossible. Cliquez pour réessayer." : undefined}
      aria-busy={busy}
      aria-expanded={multiple ? expanded : undefined}
      aria-controls={multiple ? popoverId : undefined}
      disabled={busy || (offers.campaigns.length === 0 && !listUnavailable)}
      className={`flex min-h-10 items-center gap-2 rounded-md bg-primary/10 px-3 py-2 text-sm text-primary hover:bg-primary/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ${collapsed ? "relative justify-center self-center" : "w-full text-left"}`}
      onClick={(event) => {
        if (offers.isError) {
          event.preventDefault();
          if (listUnavailable) {
            setBusy(true);
            void offers.refetch().finally(() => {
              if (active.current) setBusy(false);
            });
          }
          else setError("Actualisation impossible. Réessayez.");
          return;
        }
        visit.consumed = true;
        if (!multiple) {
          event.preventDefault();
          void open(offers.campaigns[0].id);
        }
      }}
    >
      <Sparkles className={`size-4 shrink-0 ${collapsed && busy ? "invisible" : ""}`} aria-hidden="true" />
      {!collapsed && <span>Offres du moment</span>}
      <span data-testid="current-offers-loading-slot" aria-hidden="true" className={collapsed ? "absolute right-3 size-4" : "ml-auto size-4 shrink-0"}>
        <LoaderCircle data-testid="current-offers-spinner" className={`size-4 ${busy ? "animate-spin motion-reduce:animate-none" : "invisible"}`} />
      </span>
      <span role="status" className="sr-only">{busy ? "Chargement de l’offre…" : ""}</span>
    </button>
  );
  return (
    <div>
      {(offers.campaigns.length > 0 || listUnavailable) && (
        <Popover
          open={multiple && expanded}
          onOpenChange={(value) => {
            selecting.current = false;
            setExpanded(value);
          }}
        >
          {collapsed
            ? (
              <Tooltip>
                <TooltipTrigger asChild><PopoverTrigger asChild>{button}</PopoverTrigger></TooltipTrigger>
                <TooltipContent side="right">Offres du moment</TooltipContent>
              </Tooltip>
            )
            : <PopoverTrigger asChild>{button}</PopoverTrigger>}
          <PopoverContent id={popoverId} side="top" align="start" aria-label="Offres du moment" className="max-h-[min(24rem,var(--radix-popover-content-available-height))] max-w-[calc(100vw-2rem)] overflow-y-auto p-2" onCloseAutoFocus={(event) => { if (selecting.current) event.preventDefault(); }}>
            <ul className="space-y-1">
              {offers.campaigns.map((campaign) => <li key={campaign.id}><button type="button" className="min-h-10 w-full rounded px-3 py-2 text-left break-words hover:bg-accent focus-visible:outline-2 focus-visible:outline-primary" onClick={() => void open(campaign.id)}>{campaign.title}</button></li>)}
            </ul>
          </PopoverContent>
        </Popover>
      )}
      {error && (
        <div className="text-xs">
          <p role="alert">{error}</p>
          <button
            type="button"
            className="underline"
            onClick={() => {
              if (intent.current) void open(intent.current.campaignId);
              else {
                setError("");
                void offers.refetch();
              }
            }}
          >
            Réessayer
          </button>
        </div>
      )}
    </div>
  );
};
